/**
 * DashboardPage
 *
 * Customer dashboard page displaying personalized welcome message,
 * summary stats, recent communications, upcoming payments, and activity timeline.
 *
 * Requirements: 7.1, 7.2, 7.3, 7.4 - Customer portal dashboard
 */

import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, Button } from '@bloomcode/ui';
import {
  RefreshCw,
  Plus,
  Handshake,
  FileText,
  ClipboardList,
  CalendarDays,
  MessageSquare,
} from '@bloomcode/icons';
import { useCookieSession } from '@bloomcode/auth';
import { getAppUrl } from '@bloomcode/config';
import { useCustomerDashboard } from '@/hooks/useCustomerDashboard';
import {
  CustomerStats,
  RecentCommunications,
  AccountStatusBadge,
  UpcomingPayments,
  ActivityTimeline,
} from '@/components/dashboard';
import type { Communication } from '@/components/dashboard';
import type { UpcomingPaymentItem } from '@/components/dashboard';
import type { ActivityItem } from '@/components/dashboard';

// Portal URLs for quick actions
const supportPortalUrl = getAppUrl('portal-support');

const billingPortalUrl = getAppUrl('portal-billing');

const accountPortalUrl = getAppUrl('portal-account');

// Skeleton component for the welcome section
function WelcomeSkeleton() {
  return (
    <div className="space-y-2">
      <div className="h-8 w-64 animate-pulse rounded bg-muted" />
      <div className="h-4 w-96 animate-pulse rounded bg-muted" />
    </div>
  );
}

// Quick actions card component with correct portal URLs
function QuickActions() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Quick Actions</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" asChild>
            <a href={`${supportPortalUrl}/tickets/new`}>
              <Plus className="mr-2 h-4 w-4" />
              New Support Ticket
            </a>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <a href={`${accountPortalUrl}/profile`}>View Profile</a>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <a href={`${supportPortalUrl}/tickets`}>View All Tickets</a>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <a href={`${billingPortalUrl}/invoices`}>View Invoices</a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Get Started guidance card for new customers with no data.
 * Shows when all dashboard stats are zero and there are no communications.
 */
function GetStartedCard({ navigate }: { navigate: (path: string) => void }) {
  const steps = [
    {
      icon: MessageSquare,
      title: 'Check Communications',
      description: 'View messages and announcements from your account team.',
      action: () => navigate('/communications'),
      actionLabel: 'View Communications',
    },
    {
      icon: Handshake,
      title: 'View Your Deals',
      description: 'Track proposals and quotes shared with you.',
      action: () => navigate('/deals'),
      actionLabel: 'View Deals',
    },
    {
      icon: FileText,
      title: 'Access Documents',
      description: 'Download files and documents shared by your team.',
      action: () => navigate('/documents'),
      actionLabel: 'View Documents',
    },
    {
      icon: ClipboardList,
      title: 'Fill Out Forms',
      description: 'Complete surveys, feedback forms, or requests.',
      action: () => navigate('/forms'),
      actionLabel: 'View Forms',
    },
    {
      icon: CalendarDays,
      title: 'Request a Meeting',
      description: 'Schedule time with your account representative.',
      action: () => navigate('/meetings/request'),
      actionLabel: 'Request Meeting',
    },
  ];

  return (
    <Card className="border-primary/20">
      <CardHeader>
        <CardTitle className="text-lg">Welcome to Bloomcode CRM Portal</CardTitle>
        <p className="text-sm text-muted-foreground">
          Here&apos;s what you can do in your customer portal. Your account team will share
          information with you as your relationship grows.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {steps.map((step) => (
            <button
              key={step.title}
              onClick={step.action}
              className="flex items-start gap-3 rounded-lg border border-border p-3 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={step.actionLabel}
            >
              <step.icon className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground">{step.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{step.description}</p>
              </div>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const { session } = useCookieSession();
  const navigate = useNavigate();
  const { dashboard, communications, isLoading, error, refetch } = useCustomerDashboard({
    communicationLimit: 5,
  });

  // Extract dashboard data fields
  const dashboardData = dashboard?.data ?? null;

  // Map dashboard data to CustomerStats shape
  const stats = dashboardData
    ? {
        openTickets: dashboardData.openTickets,
        recentInvoices: dashboardData.recentInvoices,
        accountStatus: dashboardData.accountStatus,
        pendingPayments: dashboardData.pendingPayments,
        totalSpent: dashboardData.totalSpent,
        lastLogin: dashboardData.lastLogin ?? undefined,
      }
    : null;

  // Map upcoming payments from dashboard data
  const upcomingPayments: UpcomingPaymentItem[] = (dashboardData?.upcomingPayments ?? []).map(
    (payment) => ({
      id: payment.id,
      amount: payment.amount,
      dueDate: payment.dueDate,
      description: payment.description,
      status: payment.status,
    })
  );

  // Map recent activity from dashboard data
  const recentActivity: ActivityItem[] = (dashboardData?.recentActivity ?? []).map((activity) => ({
    id: activity.id,
    type: activity.type,
    description: activity.description,
    date: activity.date,
    metadata: activity.metadata,
  }));

  // Get user's first name for personalized greeting
  const displayName = session?.email;
  const firstName = displayName?.split('@')[0] || 'there';

  // Get time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  // Determine if this is a new customer with no data — show Get Started guidance
  const isNewCustomer =
    stats !== null &&
    stats.openTickets === 0 &&
    stats.recentInvoices === 0 &&
    stats.pendingPayments === 0 &&
    stats.totalSpent === 0 &&
    communications.length === 0;

  // Handle communication click - navigate to appropriate page
  const handleCommunicationClick = (communication: Communication) => {
    if (communication.link) {
      navigate(communication.link);
      return;
    }

    // Navigate to communications detail
    navigate(`/communications?id=${communication.id}`);
  };

  // Error state
  if (error && !dashboard) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Helmet>
          <title>Dashboard - Bloomcode CRM Portal</title>
        </Helmet>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <p className="text-destructive mb-4">Failed to load dashboard data</p>
          <p className="text-sm text-muted-foreground mb-4">{error.message}</p>
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Dashboard - Bloomcode CRM Portal</title>
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-8 flex items-start justify-between">
          {isLoading ? (
            <WelcomeSkeleton />
          ) : (
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-3xl font-bold">
                  {getGreeting()}, {firstName}!
                </h1>
                <p className="mt-1 text-muted-foreground">
                  Here&apos;s what&apos;s happening with your account today.
                </p>
              </div>
              {stats?.accountStatus && <AccountStatusBadge status={stats.accountStatus} />}
            </div>
          )}
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="mb-8">
          <CustomerStats stats={stats} isLoading={isLoading} />
        </div>

        {/* Get Started Card — shown for new customers with no data */}
        {!isLoading && stats && isNewCustomer && (
          <div className="mb-8">
            <GetStartedCard navigate={navigate} />
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Recent Communications */}
          <RecentCommunications
            communications={communications}
            isLoading={isLoading}
            onCommunicationClick={handleCommunicationClick}
          />

          {/* Upcoming Payments */}
          <UpcomingPayments payments={upcomingPayments} isLoading={isLoading} />

          {/* Activity Timeline */}
          <ActivityTimeline activities={recentActivity} isLoading={isLoading} />

          {/* Quick Actions */}
          <QuickActions />
        </div>
      </div>
    </>
  );
}
